import { GetTokensPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/inputs/get-tokens-paginated.model';
import { GatewayTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/tokens/models/outputs/gateway-token.model';
import { GatewayGetTransactionsPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/transactions/models/inputs/gateway-get-transactions-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/create-token.model';
import { UpdateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/update-token.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  TOKEN_FILTERS_LSK,
  TOKEN_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  TRANSACTION_LIST_SORT_FIELD,
  TRANSACTION_LIST_SORT_ORDER,
} from '../transaction/transaction-list/transaction-list.config';
import {
  TOKEN_LIST_DEFAULT_OPTIONS,
  TOKEN_LIST_FILTER_NAME_LIST,
  TOKEN_LIST_SORT_FIELD,
  TOKEN_LIST_SORT_ORDER,
  TokenListOptionsModel,
} from './token-list/token-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const TokenContext = createContext<GatewayTokenModel | null>(null);

export const useTokenContext = (): GatewayTokenModel => {
  const context = useContext(TokenContext);

  if (!context) {
    throw new Error(
      'useTokenContext must be used within TokenContext.Provider',
    );
  }

  return context;
};

interface TokenContextProviderProps {
  data: GatewayTokenModel;
  children: React.ReactNode;
}

export const TokenContextProvider = ({
  data,
  children,
}: TokenContextProviderProps): React.JSX.Element => {
  return <TokenContext.Provider value={data}>{children}</TokenContext.Provider>;
};

// /////////////////////////////////////////////////////////////////////////////

export interface TokenListContextProps {
  getPaginatedQueryParameters: GetTokensPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetTokensPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: TokenListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<TokenListOptionsModel>>;
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const TokenListContext = createContext<TokenListContextProps | null>(
  null,
);

export const useTokenListContext = (): TokenListContextProps => {
  const context = useContext(TokenListContext);

  if (!context) {
    throw new Error(
      'useTokenListContext must be used within TokenListContext.Provider',
    );
  }

  return context;
};

interface TokenListContextProviderProps {
  children: React.ReactNode;
}

export const TokenListContextProvider = ({
  children,
}: TokenListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetTokensPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        valid: undefined,
        type: undefined,
        textToSearch: '',
        // Sortable fields
        sortField: TOKEN_LIST_SORT_FIELD,
        sortOrder: TOKEN_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...TOKEN_LIST_FILTER_NAME_LIST,
      ],
      TOKEN_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<TokenListOptionsModel>(
      TOKEN_LIST_DEFAULT_OPTIONS,
      TOKEN_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
    // setGetPaginatedLimitList,
  ]);

  return (
    <TokenListContext.Provider value={data}>
      {children}
    </TokenListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterTokenListContext =
  createContext<DeepPartialSkipArrayKey<GetTokensPaginatedModel> | null>(null);

export const useFilterTokenListContext =
  (): DeepPartialSkipArrayKey<GetTokensPaginatedModel> => {
    const context = useContext(FilterTokenListContext);

    if (!context) {
      throw new Error(
        'useFilterTokenListContext must be used within FilterTokenListContext.Provider',
      );
    }

    return context;
  };

interface FilterTokenListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTokensPaginatedModel>;
  children: React.ReactNode;
}

export const FilterTokenListContextProvider = ({
  data,
  children,
}: FilterTokenListContextProviderProps): React.JSX.Element => {
  return (
    <FilterTokenListContext.Provider value={data}>
      {children}
    </FilterTokenListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchTokenListContext =
  createContext<DeepPartialSkipArrayKey<GetTokensPaginatedModel> | null>(null);

export const useFilterSearchTokenListContext =
  (): DeepPartialSkipArrayKey<GetTokensPaginatedModel> => {
    const context = useContext(FilterSearchTokenListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchTokenListContext must be used within FilterSearchTokenListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchTokenListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetTokensPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchTokenListContextProvider = ({
  data,
  children,
}: FilterSearchTokenListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchTokenListContext.Provider value={data}>
      {children}
    </FilterSearchTokenListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateTokenContext =
  createContext<DeepPartialSkipArrayKey<CreateTokenModel> | null>(null);

export const useCreateTokenContext =
  (): DeepPartialSkipArrayKey<CreateTokenModel> => {
    const context = useContext(CreateTokenContext);

    if (!context) {
      throw new Error(
        'useCreateTokenContext must be used within CreateTokenContext.Provider',
      );
    }

    return context;
  };

interface CreateTokenContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateTokenModel>;
  children: React.ReactNode;
}

export const CreateTokenContextProvider = ({
  data,
  children,
}: CreateTokenContextProviderProps): React.JSX.Element => {
  return (
    <CreateTokenContext.Provider value={data}>
      {children}
    </CreateTokenContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateTokenContext = createContext<Omit<
  UpdateTokenModel,
  'customerId' | 'networkId'
> | null>(null);

export const useUpdateTokenContext = (): Omit<
  UpdateTokenModel,
  'customerId' | 'networkId'
> => {
  const context = useContext(UpdateTokenContext);

  if (!context) {
    throw new Error(
      'useUpdateTokenContext must be used within UpdateTokenContext.Provider',
    );
  }

  return context;
};

interface UpdateTokenContextProviderProps {
  data: Omit<UpdateTokenModel, 'customerId' | 'networkId'>;
  children: React.ReactNode;
}

export const UpdateTokenContextProvider = ({
  data,
  children,
}: UpdateTokenContextProviderProps): React.JSX.Element => {
  return (
    <UpdateTokenContext.Provider value={data}>
      {children}
    </UpdateTokenContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const DeleteTokenContext = createContext<DeepPartialSkipArrayKey<
  Pick<GatewayTokenModel, 'id'>
> | null>(null);

export const useDeleteTokenContext = (): DeepPartialSkipArrayKey<
  Pick<GatewayTokenModel, 'id'>
> => {
  const context = useContext(DeleteTokenContext);

  if (!context) {
    throw new Error(
      'useDeleteTokenContext must be used within DeleteTokenContext.Provider',
    );
  }

  return context;
};

interface DeleteTokenContextProviderProps {
  data: DeepPartialSkipArrayKey<Pick<GatewayTokenModel, 'id'>>;
  children: React.ReactNode;
}

export const DeleteTokenContextProvider = ({
  data,
  children,
}: DeleteTokenContextProviderProps): React.JSX.Element => {
  return (
    <DeleteTokenContext.Provider value={data}>
      {children}
    </DeleteTokenContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface TokenTransactionListContextProps {
  getPaginatedQueryParameters: GatewayGetTransactionsPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GatewayGetTransactionsPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const TokenTransactionListContext =
  createContext<TokenTransactionListContextProps | null>(null);

export const useTokenTransactionListContext =
  (): TokenTransactionListContextProps => {
    const context = useContext(TokenTransactionListContext);

    if (!context) {
      throw new Error(
        'useTokenTransactionListContext must be used within TokenTransactionListContext.Provider',
      );
    }

    return context;
  };

interface TokenTransactionListContextProviderProps {
  data: { token: GatewayTokenModel };
  children: React.ReactNode;
}

export const TokenTransactionListContextProvider = ({
  data,
  children,
}: TokenTransactionListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GatewayGetTransactionsPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      tokenId: [data.token.id],
      // Sortable fields
      sortField: TRANSACTION_LIST_SORT_FIELD,
      sortOrder: TRANSACTION_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <TokenTransactionListContext.Provider value={value}>
      {children}
    </TokenTransactionListContext.Provider>
  );
};
